import { Box, Button, Card, Group, Stack, Text } from '@mantine/core';
import { IconDownload, IconPaperclip } from '@tabler/icons-react';
import { iconProps } from 'components/icon-props';
import { i18nKeys, useTranslation } from 'locales';
import type { Mail } from 'modules/Mails/hooks';
import { useState } from 'react';
import { useThreadMessage } from './useThreadMessage';
import { downloadFromUrl, getExtension } from './utils';

export const ThreadMessageAttachments = () => {
  const { t } = useTranslation();
  const { mail } = useThreadMessage();

  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const handleDownloadAll = async () => {
    setIsDownloadingAll(true);
    try {
      for (const attachment of mail.attachments) {
        try {
          await downloadFromUrl(attachment.url, attachment.filename);
        } catch {
          window.open(attachment.url, '_blank', 'noopener,noreferrer');
        }
      }
    } finally {
      setIsDownloadingAll(false);
    }
  };

  return (
    <Stack p="space5" gap="space2">
      <Group gap="space2" justify="space-between">
        <Group gap="space2">
          <IconPaperclip {...iconProps(13)} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            {mail.attachments.length}{' '}
            {t(i18nKeys.EXTERNAL_MAIL.ATTACHMENTS, {
              count: mail.attachments.length,
            }).toLowerCase()}
          </Text>
        </Group>
        {mail.attachments.length > 1 && (
          <Button
            variant="subtle"
            size="compact-xs"
            color="gray"
            leftSection={<IconDownload {...iconProps(12)} />}
            onClick={handleDownloadAll}
            loading={isDownloadingAll}
          >
            {t(i18nKeys.EXTERNAL_MAIL.DETAIL_NEXT.DOWNLOAD_ALL)}
          </Button>
        )}
      </Group>
      <Group gap="space2" wrap="wrap">
        {mail.attachments.map(attachment => (
          <AttachmentCard key={attachment.url} attachment={attachment} />
        ))}
      </Group>
    </Stack>
  );
};

type AttachmentCardProps = {
  attachment: Mail['attachments'][number];
};

const AttachmentCard = ({ attachment }: AttachmentCardProps) => {
  const extension = getExtension(attachment.filename);

  return (
    <Card
      component="a"
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      withBorder
      padding="space2"
      radius="sm"
      style={{ minWidth: 200, textDecoration: 'none' }}
    >
      <Group gap="space2" wrap="nowrap">
        <Box
          bg="gray.1"
          c="dimmed"
          px={6}
          py={2}
          ff="monospace"
          fz={10}
          fw={600}
          tt="uppercase"
          bdrs={4}
        >
          {extension}
        </Box>
        <Text size="sm" truncate style={{ flex: 1 }}>
          {attachment.filename}
        </Text>
      </Group>
    </Card>
  );
};
